import { Controller } from "@hotwired/stimulus"

const euros = new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", minimumFractionDigits: 0, maximumFractionDigits: 2 });

// Price grid of a pricing policy: row totals, group inputs that fill every price below, folding.
export default class extends Controller {
  static targets = ["row", "input", "groupInput", "total"];
  static values = { mixed: String };

  connect() {
    this.updateShadows = () => {
      const { scrollLeft, scrollWidth, clientWidth } = this.element;
      this.element.classList.toggle("pricing-grid--scrolled", scrollLeft > 0);
      this.element.classList.toggle("pricing-grid--overflowing", scrollLeft + clientWidth < scrollWidth - 1);
    };
    this.element.addEventListener("scroll", this.updateShadows, { passive: true });
    // Also fires when a folded accordion opens, the grid only gets a width then.
    this.resizeObserver = new ResizeObserver(this.updateShadows);
    this.resizeObserver.observe(this.element);
  }

  disconnect() {
    this.element.removeEventListener("scroll", this.updateShadows);
    this.resizeObserver.disconnect();
  }

  blockExponent(event) {
    if (event.key === "e" || event.key === "E") event.preventDefault();
  }

  leafChanged(event) {
    this.refreshTotal(event.target.closest("tr"));
    this.refreshGroups();
  }

  groupChanged(event) {
    const { path, column } = event.target.dataset;
    this.leafRowsUnder(path).forEach((row) => {
      const input = row.querySelector(`[data-pricing-grid-target="input"][data-column="${column}"]`);
      if (input) input.value = event.target.value;
      this.refreshTotal(row);
    });
    this.refreshGroups(event.target);
  }

  toggle(event) {
    const button = event.currentTarget;
    button.setAttribute("aria-expanded", button.getAttribute("aria-expanded") !== "true");

    const expanded = new Set(
      [...this.element.querySelectorAll('.pricing-grid__toggle[aria-expanded="true"]')].map((b) => b.dataset.path)
    );
    this.rowTargets.forEach((row) => {
      row.hidden = !this.ancestorsOf(row).every((path) => expanded.has(path));
    });
  }

  refreshTotal(row) {
    const total = row.querySelector('[data-pricing-grid-target="total"]');
    if (total) total.textContent = euros.format(this.rowTotal(row));
  }

  // Each group shows the amount shared by all the prices below it, or "Variable" when they differ.
  refreshGroups(source = null) {
    this.rowTargets.filter((row) => row.classList.contains("pricing-grid__group")).forEach((group) => {
      const leaves = this.leafRowsUnder(group.dataset.path);

      group.querySelectorAll('[data-pricing-grid-target="groupInput"]').forEach((input) => {
        if (input === source) return;
        const values = new Set(leaves.map((row) => this.inputValue(row, input.dataset.column)));
        input.value = values.size === 1 ? [...values][0] : "";
        input.placeholder = values.size === 1 ? "" : this.mixedValue;
      });

      const totals = leaves.map((row) => this.rowTotal(row));
      const total = group.querySelector('[data-pricing-grid-target="total"]');
      if (total && totals.length) total.textContent = this.range(Math.min(...totals), Math.max(...totals));
    });
  }

  leafRowsUnder(path) {
    return this.rowTargets.filter((row) => row.classList.contains("pricing-grid__leaf") && this.ancestorsOf(row).includes(path));
  }

  ancestorsOf(row) {
    return row.dataset.ancestors.split(" ").filter(Boolean);
  }

  inputValue(row, column) {
    return row.querySelector(`[data-pricing-grid-target="input"][data-column="${column}"]`)?.value ?? "";
  }

  // Rounded to the cent: summing floats leaves artefacts such as 0.30000000000000004.
  rowTotal(row) {
    const sum = [...row.querySelectorAll('[data-pricing-grid-target="input"]')]
      .reduce((acc, input) => acc + (parseFloat(input.value) || 0), parseFloat(row.dataset.base) || 0);
    return Math.round(sum * 100) / 100;
  }

  range(min, max) {
    return min === max ? euros.format(min) : `${euros.format(min)} – ${euros.format(max)}`;
  }
}
